import { fmtDateTime } from '../../lib/format';
import React, { useEffect, useMemo, useState } from 'react';
import { message, Modal, Input as AntInput } from 'antd';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Inbox, Loader2, Check, X, Building2, Mail, Hash, RefreshCw, KeyRound } from 'lucide-react';
import { useAppSelector } from '../../app/hooks';
import { selectHasPermission } from '../../features/auth/authSlice';
import {
  listApplications,
  approveApplication,
  rejectApplication,
  revealApplicationCode,
  type TenantApplication,
  type ApplicationStatus,
} from '../../services/tenantService';


const STATUS_TABS: { key: string; label: string }[] = [
  { key: 'submitted', label: 'Pending review' },
  { key: 'approved', label: 'Awaiting verification' },
  { key: 'completed', label: 'Completed' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'all', label: 'All' },
];

// Theme-aware semantic badges (soft tint + strong text) so they read correctly in
// both light and dark, instead of fixed pastel-100 tints that glare on dark surfaces.
const STATUS_BADGE: Record<ApplicationStatus, string> = {
  submitted: 'bg-warning/15 text-warning',
  approved: 'bg-info/15 text-info',
  completed: 'bg-success/15 text-success',
  rejected: 'bg-danger/15 text-danger',
  expired: 'bg-muted text-muted-foreground',
};

const STATUS_LABEL: Record<ApplicationStatus, string> = {
  submitted: 'Pending',
  approved: 'Code sent',
  completed: 'Completed',
  rejected: 'Rejected',
  expired: 'Expired',
};

export const TenantApplicationsPage: React.FC = () => {
  const canReview = useAppSelector(selectHasPermission('tenant:create'));

  const [tab, setTab] = useState<string>('submitted');
  const [apps, setApps] = useState<TenantApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = async (status: string) => {
    setLoading(true);
    try {
      const res = await listApplications(status === 'all' ? undefined : status);
      setApps(Array.isArray(res) ? res : []);
    } catch {
      message.error('Could not load applications');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load(tab);
  }, [tab]);

  const pendingCount = useMemo(
    () => apps.filter((a) => a.status === 'submitted').length,
    [apps],
  );

  const onApprove = (app: TenantApplication) => {
    Modal.confirm({
      title: `Approve ${app.companyName}?`,
      content:
        'This emails a 6-digit code to the applicant so they can set a password and create their account. No tenant is created until they verify.',
      okText: 'Approve & send code',
      okButtonProps: { style: { background: 'var(--color-success)', borderColor: 'var(--color-success)' } },
      onOk: async () => {
        setBusyId(app.id);
        try {
          await approveApplication(app.id);
          message.success(`Approved — verification code emailed to ${app.email}`);
          await load(tab);
        } catch {
          message.error('Could not approve application');
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  const onReject = (app: TenantApplication) => {
    let reason = '';
    Modal.confirm({
      title: `Reject ${app.companyName}?`,
      content: (
        <div className="mt-2">
          <p className="text-sm text-muted-foreground mb-2">
            Optionally tell the applicant why (included in the email).
          </p>
          <AntInput.TextArea
            rows={3}
            maxLength={500}
            placeholder="Reason (optional)…"
            onChange={(e) => {
              reason = e.target.value;
            }}
          />
        </div>
      ),
      okText: 'Reject',
      okButtonProps: { danger: true },
      onOk: async () => {
        setBusyId(app.id);
        try {
          await rejectApplication(app.id, reason.trim() || undefined);
          message.success(`${app.companyName} rejected`);
          await load(tab);
        } catch {
          message.error('Could not reject application');
        } finally {
          setBusyId(null);
        }
      },
    });
  };

  const onRevealCode = async (app: TenantApplication) => {
    setBusyId(app.id);
    try {
      const r = await revealApplicationCode(app.id);
      Modal.info({
        title: `Verification code for ${r.companyName}`,
        width: 480,
        okText: 'Done',
        content: (
          <div className="mt-2 space-y-3">
            <p className="text-sm text-muted-foreground">
              Share this with <span className="font-medium">{r.email}</span> directly (WhatsApp/phone) if the
              email didn’t arrive. It <span className="font-medium">replaces any previous code</span> and expires in 24h.
            </p>
            <div className="text-3xl font-mono font-bold tracking-[0.3em] text-center py-3 bg-muted rounded-lg select-all">
              {r.code}
            </div>
            <div>
              <p className="text-sm mb-1">Verification link:</p>
              <AntInput readOnly value={r.verifyUrl} onFocus={(e) => e.target.select()} />
            </div>
          </div>
        ),
      });
    } catch {
      message.error('Could not reveal a code — make sure the application is approved.');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex-1">
          <h1 className="text-h2 text-foreground">Company applications</h1>
          <p className="text-muted-foreground mt-1">
            Review and verify companies applying to join the platform.
          </p>
        </div>
        <Button variant="outline" onClick={() => load(tab)} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {STATUS_TABS.map((t) => (
            <TabsTrigger key={t.key} value={t.key}>
              {t.label}
              {t.key === 'submitted' && pendingCount > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[11px]">
                  {pendingCount}
                </span>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Inbox className="w-5 h-5" /> Applications ({apps.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-12 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
            </div>
          ) : apps.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">No applications here.</div>
          ) : (
            <div className="divide-y divide-border">
              {apps.map((a) => (
                <div key={a.id} className="py-4 flex items-start gap-4 flex-wrap">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-[10px] bg-orange-100 dark:bg-orange-500/[0.12] text-orange-700 shrink-0">
                    <Building2 className="w-5 h-5" />
                  </span>

                  <div className="flex-1 min-w-[220px]">
                    <div className="font-medium text-foreground">{a.companyName}</div>
                    <div className="flex flex-col gap-0.5 mt-1 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5" /> {a.email}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <Hash className="w-3.5 h-3.5" /> TIN {a.tin}
                      </span>
                    </div>
                    {a.status === 'rejected' && a.rejectionReason && (
                      <div className="mt-2 text-sm text-red-600 dark:text-red-400">Reason: {a.rejectionReason}</div>
                    )}
                  </div>

                  <div className="flex flex-col items-end gap-1 min-w-[160px]">
                    <Badge className={STATUS_BADGE[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                    <span className="text-xs text-muted-foreground">{fmtDateTime(a.createdAt)}</span>
                  </div>

                  {a.status === 'submitted' && canReview && (
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        className="bg-green-600 hover:bg-green-700 text-white"
                        disabled={busyId === a.id}
                        onClick={() => onApprove(a)}
                      >
                        {busyId === a.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Check className="w-4 h-4 mr-1" />
                        )}
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200 hover:bg-red-50 dark:bg-red-500/10"
                        disabled={busyId === a.id}
                        onClick={() => onReject(a)}
                      >
                        <X className="w-4 h-4 mr-1" /> Reject
                      </Button>
                    </div>
                  )}

                  {a.status === 'approved' && canReview && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busyId === a.id}
                      onClick={() => onRevealCode(a)}
                      title="Email didn't arrive? Get a code to share with the applicant directly."
                    >
                      {busyId === a.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <KeyRound className="w-4 h-4 mr-1" />
                      )}
                      Reveal code
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TenantApplicationsPage;
